import {
  Badge,
  EmptyState,
  PageHeader,
  StatusBadge,
  type CalendarBadgeStatus,
  type ContractBadgeStatus,
  type CustomerBadgeStatus,
  type MenuBadgeStatus,
  type OrderBadgeStatus,
  type ProductionBadgeStatus,
} from '@catering/ui';
import { Link, getRouteApi } from '@tanstack/react-router';
import { HelpCircle } from 'lucide-react';
import type { JSX } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { GUIDE_MODULES } from '../../guide/content';
import type { GuideModule } from '../../guide/types';

const guideModuleRoute = getRouteApi('/protected/guia/$moduleId');

function resolveModule(moduleId: string): GuideModule | undefined {
  return GUIDE_MODULES.find((module) => module.id === moduleId);
}

/**
 * `design.md` §7: reuse `StatusBadge` by the module's real domain so the
 * colours in the Guide match the colours the actual screen uses — the
 * content's own `state.label` only backs modules without a mapped domain
 * yet (e.g. a future module before its `StatusBadge` domain exists).
 */
function renderStateBadge(moduleId: string, state: GuideModule['states'][number]): JSX.Element {
  switch (moduleId) {
    case 'customers':
      return <StatusBadge domain="customer" status={state.value as CustomerBadgeStatus} />;
    case 'contracts':
      return <StatusBadge domain="contract" status={state.value as ContractBadgeStatus} />;
    case 'catalog':
      return <StatusBadge domain="menu" status={state.value as MenuBadgeStatus} />;
    case 'calendar':
      return <StatusBadge domain="calendar" status={state.value as CalendarBadgeStatus} />;
    case 'orders':
      return <StatusBadge domain="order" status={state.value as OrderBadgeStatus} />;
    case 'production':
      return <StatusBadge domain="production" status={state.value as ProductionBadgeStatus} />;
    default:
      return <Badge>{state.label}</Badge>;
  }
}

/** `guide-navigation/spec.md` and `guide-access/spec.md`. */
export function GuideModulePage(): JSX.Element {
  const { moduleId } = guideModuleRoute.useParams();
  const currentUser = useCurrentUser();

  const module = resolveModule(moduleId);

  if (module === undefined) {
    return (
      <EmptyState
        headingLevel="h1"
        icon={HelpCircle}
        title="No encontramos esa guía"
        description="Todavía no hay contenido documentado para este módulo."
        action={
          <Link to="/guia" className="text-sm font-medium text-primary-600 hover:underline">
            Volver a Guía
          </Link>
        }
      />
    );
  }

  const canView =
    module.requiredPermission === null ||
    hasPermission(currentUser.data, module.requiredPermission);

  if (!canView) {
    return (
      <EmptyState
        headingLevel="h1"
        icon={HelpCircle}
        title="No tenés permiso para ver esta sección"
        description="Pedile a un administrador que te asigne el permiso correspondiente si creés que deberías tener acceso."
        action={
          <Link to="/guia" className="text-sm font-medium text-primary-600 hover:underline">
            Volver a Guía
          </Link>
        }
      />
    );
  }

  const Icon = module.icon;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader icon={Icon} title={module.title} description={module.summary} />

      <div id="steps" className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-slate-700">Paso a paso</h2>
        <ol className="flex flex-col gap-3">
          {module.steps.map((step, index) => (
            <li key={step.title} className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-600">
                {index + 1}
              </span>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-900">{step.title}</span>
                <span className="text-sm text-slate-600">{step.description}</span>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {module.states.length > 0 ? (
        <div id="states" className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-slate-700">Qué significa cada estado</h2>
          <ul className="flex flex-col gap-2">
            {module.states.map((state) => (
              <li key={state.value} className="flex items-center gap-3">
                {renderStateBadge(module.id, state)}
                <span className="text-sm text-slate-600">{state.description}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div id="faq" className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-slate-700">Preguntas frecuentes</h2>
        <dl className="flex flex-col gap-3">
          {module.faq.map((item) => (
            <div key={item.question} className="flex flex-col gap-0.5">
              <dt className="text-sm font-medium text-slate-900">{item.question}</dt>
              <dd className="text-sm text-slate-600">{item.answer}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div id="tips" className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-slate-700">Consejos</h2>
        <ul className="list-disc pl-5 text-sm text-slate-600">
          {module.tips.map((tip) => (
            <li key={tip}>{tip}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}
